import { twMerge } from "tailwind-merge";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  H4,
  H6,
  InputText,
} from "@sellar-io/components";
import { SupplierGetResponseType } from "@sellar-io/api-v2/types";
import { useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { Icon } from "../../../components";
import { Alert } from "../../../components/alert/Alert";
import { ReactHookFormSwitch } from "../../../components/form/ReactHookFormSwitch";
import { InputLabel, InputContainer } from "@sellar-io/components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../components/modal/Dialog";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { useSupplierAnnouncementCreate } from "../../../hooks/api/v2/supplierAnnouncement/useSupplierAnnouncementCreate";
import { useSupplierAnnouncementUpdate } from "../../../hooks/api/v2/supplierAnnouncement/useSupplierAnnouncementUpdate";
import { useSellarSupplier } from "../../../hooks/useSellarSupplier";

export const StorefrontSettingsAnnouncementBannerCard = ({
  supplierAnnouncement,
}: {
  supplierAnnouncement: SupplierGetResponseType["SupplierAnnouncement"];
}) => {
  const [modalActive, setModalActive] = useState(false);

  return (
    <Card className="bg-white">
      <CardHeader
        className={twMerge(
          "bg-white",
          "flex items-center justify-between gap-6 p-4",
        )}
      >
        <div>
          <H4>Announcement banner</H4>
          <H6>
            Announce something important to your customers such as a new product
            launch, a flash sale or anything you like.
          </H6>
        </div>
        <Button color={"secondary"} onClick={() => setModalActive(true)}>
          <Icon icon={"PENCIL"} />
          Edit
        </Button>
      </CardHeader>
      <CardContent className={"p-4"}>
        {[
          "Your announcement banner is currently:",
          supplierAnnouncement?.showAnnouncement ? "SHOWN" : "HIDDEN",
        ].join(" ")}
      </CardContent>
      <ConfigureStoreSettingsSupplierAnnouncementModal
        supplierAnnouncement={supplierAnnouncement}
        active={modalActive}
        onDismiss={() => setModalActive(false)}
      />
    </Card>
  );
};

const ConfigureStoreSettingsSupplierAnnouncementModal = ({
  supplierAnnouncement,
  active,
  onDismiss,
}: {
  supplierAnnouncement: SupplierGetResponseType["SupplierAnnouncement"];
  active: boolean;
  onDismiss: () => void;
}) => {
  const supplier = useSellarSupplier();

  const supplierAnnouncementCreateMutation = useSupplierAnnouncementCreate(
    supplier.id,
  );

  const supplierAnnouncementUpdateMutation = useSupplierAnnouncementUpdate(
    supplier.id,
    supplierAnnouncement?.id ?? 0,
  );

  const { register, handleSubmit, control, formState, reset } = useForm({
    defaultValues: supplierAnnouncement ?? {},
    shouldUseNativeValidation: true,
  });
  const ctaLabel = useWatch({
    control,
    name: "ctaLabel",
  });

  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        title={"Add an announcement"}
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
      />
      {supplierAnnouncementCreateMutation.isError && (
        <Alert
          message={
            apiV2AssertError(supplierAnnouncementCreateMutation.error)?.response
              ?.data.message ?? "Unknown error"
          }
        />
      )}
      <form
        onSubmit={handleSubmit((data) => {
          if (!supplierAnnouncement) {
            supplierAnnouncementCreateMutation.mutate(data, {
              onSuccess: (response) => {
                reset(response);
                onDismiss();
              },
            });
          } else {
            supplierAnnouncementUpdateMutation.mutate(data, {
              onSuccess: (response) => {
                reset(response);
                onDismiss();
              },
            });
          }
        })}
      >
        <DialogContent className={"flex flex-col gap-4"}>
          <div className={"flex justify-between"}>
            <InputLabel>Show announcement</InputLabel>
            <ReactHookFormSwitch
              {...register("showAnnouncement", { required: false })}
              control={control}
            />
          </div>
          <div>
            <InputLabel>Announcement</InputLabel>
            <InputContainer>
              <InputText
                {...register("message", { required: false })}
                maxLength={100}
              />
            </InputContainer>
          </div>
          <div>
            <InputLabel>Button label</InputLabel>
            <InputContainer>
              <InputText
                {...register("ctaLabel", { required: false })}
                placeholder={"Order now"}
                maxLength={20}
              />
            </InputContainer>
          </div>
          <div>
            <InputLabel>Button URL</InputLabel>
            <InputContainer>
              <InputText {...register("ctaLink", { required: !!ctaLabel })} />
            </InputContainer>
          </div>
        </DialogContent>

        <DialogCTA>
          <Button
            type={"submit"}
            className={"w-full"}
            loading={
              supplierAnnouncementUpdateMutation.isPending ||
              supplierAnnouncementCreateMutation.isPending
            }
            disabled={!formState.isDirty}
          >
            Update
          </Button>
        </DialogCTA>
      </form>
    </Dialog>
  );
};
